import { useState } from 'react';
import { Icon } from '@ah/ui';
import type { CrisisBarContent } from '@ah/contracts';

/**
 * Crisis line — ethically mandatory (brief §1).
 *
 * Renders above the header on every page and has no dismiss control and no
 * `enabled` prop: there is deliberately no code path that hides it. The
 * dashboard cannot switch it off either — `readSection` forces
 * `enabled_locked` back to true and restores the numbers if a stored draft
 * emptied them.
 *
 * Amber-soft background rather than red: someone in crisis does not need an
 * alarm, they need a number they can find. Red also reads as an error banner
 * and gets dismissed by pattern-matching before it is read.
 */
export function CrisisBar({ content }: { content: CrisisBarContent }) {
  const [open, setOpen] = useState(false);

  return (
    <aside style={bar} aria-label="Γραμμές άμεσης βοήθειας">
      <div style={inner}>
        <p style={text}>{content.text}</p>
        <button type="button" onClick={() => setOpen((v) => !v)} style={toggle} aria-expanded={open}>
          {content.link_label}
          <Icon
            name="chevron-down"
            size={16}
            style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease' }}
          />
        </button>
      </div>

      {open && (
        <ul style={list}>
          {content.lines.map((line) => (
            <li key={line.value} style={{ margin: 0 }}>
              <a href={`tel:${line.value}`} style={link}>
                <Icon name="phone" size={15} />
                {line.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

const bar: React.CSSProperties = {
  background: 'var(--ah-color-accent-soft)',
  borderBottom: '1px solid var(--ah-color-border)',
  color: 'var(--ah-color-text)',
};

const inner: React.CSSProperties = {
  maxWidth: 1080,
  margin: '0 auto',
  padding: '10px var(--ah-space-3)',
  display: 'flex',
  gap: 'var(--ah-space-3)',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const text: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.45,
  textAlign: 'center',
};

const toggle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--ah-color-primary-ink)',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

const list: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: '0 var(--ah-space-3) 12px',
  maxWidth: 1080,
  marginInline: 'auto',
  display: 'flex',
  gap: 'var(--ah-space-4)',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const link: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 14,
  color: 'var(--ah-color-text)',
  textDecoration: 'none',
  fontWeight: 600,
};
